import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { getFileIcon } from "../../utils/fileIconUtils";
import { getLinkIcon } from "../../utils/businessCardUtils";
import {
  HiChevronDown,
  HiChevronUp,
  HiOutlineDocumentText,
  HiOutlineDownload,
  HiOutlineMail,
  HiOutlinePhone,
  HiOutlineUser,
} from "react-icons/hi";
import VCardGenerator from "./VCardGenerator";
import { LinkItem } from "./LinkManager";
import BusinessCardFooter from "./BusinessCardFooter";
import BusinessCardShareButton from "./BusinessCardShareButton";
import UserRegistrationSection from "./UserRegistrationSection";
import BookingLinkButton from "./BookingLinkButton";
import { BusinessCardBooking } from "../../types/BusinessCardTypes";
import BusinessCardContainer from "./BusinessCardContainer";
import BusinessCardEmailLogin from "../pages/businessCardView/BusinessCardEmailLogin";
import ExpandableDescription from "./ExpandableDescription";
import LanguageSelector from "./LanguageSelector";
import SmartLink from "./SmartLink";
import PreviewSafeAnchor from "./PreviewSafeAnchor";
import PreviewSafeButton from "./PreviewSafeButton";

interface BusinessCardData {
  jobTitle: string;
  description?: string;
  links: LinkItem[];
  uploadedFiles?: Array<{
    fileName: string;
    cdnPath: string;
    displayName: string;
  }>;
  allowVCardDownload?: boolean;
  enableUserRegistration?: boolean;
  showFooter?: boolean;
  booking?: BusinessCardBooking;
}

interface BusinessCardCreativeDisplayProps {
  userData: {
    user_identification: string;
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    avatar?: string;
  };
  businessCardData: BusinessCardData;
  companyLogo?: string;
  companyName?: string;
  companyDescription?: string;
  className?: string;
  isPreview?: boolean;
  processId?: string;
  enableUserRegistration?: boolean;
  entityIdentification: string;
  ownerIdentification: string;
  onEmailClick?: () => void;
}

const BusinessCardCreativeDisplay: React.FC<
  BusinessCardCreativeDisplayProps
> = (props) => {
  const {
    userData,
    businessCardData,
    companyLogo,
    companyName,
    companyDescription,
    className = "",
    isPreview = false,
    processId,
    enableUserRegistration = false,
    entityIdentification,
    ownerIdentification,
    onEmailClick,
  } = props;
  const { t } = useTranslation("clientView");
  const [showAllLinks, setShowAllLinks] = useState(false);
  const [showFiles, setShowFiles] = useState(false);
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [isFlipping, setIsFlipping] = useState(false);

  const handleFlipToEmail = () => {
    if (isFlipping) return;
    setIsFlipping(true);
    setTimeout(() => {
      setShowEmailLogin(true);
      onEmailClick?.();
    }, 300);
    setTimeout(() => setIsFlipping(false), 600);
  };

  const handleFileDownload = (cdnPath: string, fileName: string) => {
    const link = document.createElement("a");
    link.href = cdnPath;
    link.download = fileName;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const primaryLinks = businessCardData.links?.slice(0, 4) || [];
  const additionalLinks = businessCardData.links?.slice(4) || [];
  const hasFiles =
    businessCardData.uploadedFiles && businessCardData.uploadedFiles.length > 0;

  const headerContent = (companyName || companyLogo || companyDescription) && (
    <div className="relative bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 flex-shrink-0">
      <div className="absolute top-0 right-0 w-20 h-20 bg-white bg-opacity-10 rounded-full -translate-y-10 translate-x-10"></div>
      <div className="absolute bottom-0 left-0 w-16 h-16 bg-white bg-opacity-20 rounded-lg rotate-45 -translate-x-8 translate-y-8"></div>

      <div className="relative z-10 px-3 sm:px-4 py-2 sm:py-3">
        <div className="absolute top-2 end-3 z-[9999]">
          <LanguageSelector variant="compact" />
        </div>
        <div className="p-2 bg-white bg-opacity-20 backdrop-blur-sm rounded-xl">
          {companyName && (
            <p
              className={`${
                companyName.length > 20 ? "text-xs" : "text-sm"
              } font-bold text-white drop-shadow-sm text-start`}
            >
              <bdi>{companyName}</bdi>
            </p>
          )}
          {(companyLogo || companyDescription) && (
            <div className="flex items-start gap-2 mt-1">
              {companyLogo && (
                <div className="w-10 h-10 flex-shrink-0">
                  <img
                    src={companyLogo}
                    alt="Company Logo"
                    className="w-full h-full object-contain rounded-lg bg-white opacity-90"
                  />
                </div>
              )}
              {companyDescription && (
                <p className="text-xs text-white text-opacity-90 italic text-start">
                  <bdi>"{companyDescription}"</bdi>
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const mainContent = showEmailLogin ? (
    <div className="flex flex-col gap-3 sm:gap-4">
      <BusinessCardEmailLogin
        entityIdentification={entityIdentification}
        ownerIdentification={ownerIdentification}
        onBackToCard={() => {
          if (isFlipping) return;
          setIsFlipping(true);
          setTimeout(() => setShowEmailLogin(false), 300);
          setTimeout(() => setIsFlipping(false), 600);
        }}
      />
    </div>
  ) : (
    <div className="flex flex-col gap-3 sm:gap-4">
      <div className="pb-3 border-b border-purple-100">
        <div className="relative bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 p-4 rounded-xl">
          <div className="absolute top-0 right-0 w-16 h-16 bg-white bg-opacity-10 rounded-full -translate-y-8 translate-x-8"></div>
          <div className="absolute bottom-0 left-0 w-12 h-12 bg-white bg-opacity-20 rounded-lg rotate-45 -translate-x-6 translate-y-6"></div>

          <div className="relative z-10 flex items-center gap-4">
            {userData.avatar ? (
              <img
                src={userData.avatar}
                alt={`${userData.first_name} ${userData.last_name}`}
                className="w-16 h-16 rounded-full border-3 border-white object-cover shadow-lg"
              />
            ) : (
              <div className="w-16 h-16 bg-white bg-opacity-30 rounded-full flex items-center justify-center border-3 border-white shadow-lg backdrop-blur-sm">
                {userData.first_name?.[0] || userData.last_name?.[0] ? (
                  <span className="text-xl font-bold text-white uppercase">
                    {userData.first_name?.[0]}
                    {userData.last_name?.[0]}
                  </span>
                ) : (
                  <HiOutlineUser className="w-8 h-8 text-white" />
                )}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h1 className="text-base sm:text-lg font-bold text-white drop-shadow-sm break-words">
                <bdi>
                  {userData.first_name} {userData.last_name}
                </bdi>
              </h1>
              <p className="text-sm text-white text-opacity-90 font-medium break-words mt-1 text-start">
                <bdi>{businessCardData.jobTitle}</bdi>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <PreviewSafeAnchor
          href={`mailto:${userData.email}`}
          className="grid grid-cols-[auto_1fr] gap-2 sm:gap-3 w-full p-2.5 sm:p-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-200 shadow-sm hover:shadow-lg transform hover:scale-[1.02] items-center"
        >
          <HiOutlineMail className="w-5 h-5 flex-shrink-0" />
          <div className="min-w-0">
            <p
              dir="auto"
              className="text-xs text-purple-100 font-medium text-start"
            >
              {t("key98")}
            </p>
            <p
              className="font-medium text-xs sm:text-sm truncate"
              title={userData.email}
            >
              <bdi>{userData.email}</bdi>
            </p>
          </div>
        </PreviewSafeAnchor>

        {userData.phone && (
          <PreviewSafeAnchor
            href={`tel:${userData.phone}`}
            className="flex items-center gap-2 sm:gap-3 w-full p-2.5 sm:p-3 bg-gradient-to-r from-pink-500 to-orange-400 text-white rounded-xl hover:from-pink-600 hover:to-orange-500 transition-all duration-200 shadow-sm hover:shadow-lg transform hover:scale-[1.02]"
          >
            <HiOutlinePhone className="w-5 h-5 flex-shrink-0" />
            <p className="font-medium text-xs sm:text-sm">
              <bdi>{userData.phone}</bdi>
            </p>
          </PreviewSafeAnchor>
        )}
      </div>

      {businessCardData.description && (
        <div className="p-1 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-100">
          <ExpandableDescription
            content={businessCardData.description}
            maxLength={150}
            maxLines={5}
            expandText={t("key97") as string}
            collapseText={t("key107") as string}
            textClassName="text-sm text-gray-700 leading-relaxed font-medium"
            buttonClassName="mt-2 inline-flex items-center text-xs font-medium text-purple-600 hover:text-purple-800 transition-colors duration-200"
          />
        </div>
      )}

      {primaryLinks.length > 0 && (
        <div className="space-y-2">
          <h4
            dir="auto"
            className="text-xs font-bold text-gray-800 uppercase tracking-widest text-start"
          >
            {t("key104")}
          </h4>
          <div className="grid grid-cols-2 gap-2">
            {primaryLinks.map((link, index) => (
              <SmartLink
                key={index}
                href={link.link}
                fallbackHref={link.fallbackLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 p-3 bg-gray-50 hover:bg-gradient-to-br hover:from-purple-50 hover:to-pink-50 rounded-xl transition-all duration-200 group border hover:border-purple-200"
              >
                <div>{getLinkIcon(link, "text-purple-600")}</div>
                <p className="text-xs font-semibold text-gray-700 leading-tight w-full truncate text-start">
                  {link.label}
                </p>
              </SmartLink>
            ))}
          </div>
        </div>
      )}

      {additionalLinks.length > 0 && (
        <div>
          {showAllLinks && (
            <div className="grid grid-cols-2 gap-2 mb-2">
              {additionalLinks.map((link, index) => (
                <SmartLink
                  key={index + 3}
                  href={link.link}
                  fallbackHref={link.fallbackLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-2 p-3 bg-gray-50 hover:bg-gradient-to-br hover:from-purple-50 hover:to-pink-50 rounded-xl transition-all duration-200 group border hover:border-purple-200"
                >
                  <div>{getLinkIcon(link, "text-purple-600")}</div>
                  <p className="text-xs font-semibold text-gray-700 leading-tight w-full truncate text-start">
                    {link.label}
                  </p>
                </SmartLink>
              ))}
            </div>
          )}
          <PreviewSafeButton
            onClick={() => setShowAllLinks(!showAllLinks)}
            className="flex items-center justify-center w-full p-2 text-sm text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition-colors duration-200"
            dir="auto"
          >
            <span className="me-1 text-start">
              {showAllLinks
                ? t("businessCard.showLess")
                : t("businessCard.showMoreLinks", {
                    count: additionalLinks.length,
                  })}
            </span>
            {showAllLinks ? (
              <HiChevronUp className="w-4 h-4" />
            ) : (
              <HiChevronDown className="w-4 h-4" />
            )}
          </PreviewSafeButton>
        </div>
      )}

      {hasFiles && (
        <div>
          <PreviewSafeButton
            onClick={() => setShowFiles(!showFiles)}
            className="flex items-center justify-between w-full p-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-colors duration-200"
            dir="auto"
          >
            <div className="flex items-center gap-2">
              <HiOutlineDocumentText className="w-4 h-4" />
              <span className="text-start">
                {t("businessCard.files", {
                  count: businessCardData.uploadedFiles?.length || 0,
                })}
              </span>
            </div>
            {showFiles ? (
              <HiChevronUp className="w-4 h-4" />
            ) : (
              <HiChevronDown className="w-4 h-4" />
            )}
          </PreviewSafeButton>

          {showFiles && businessCardData.uploadedFiles && (
            <div className="mt-2 space-y-1">
              {businessCardData.uploadedFiles.map((file, index) => (
                <PreviewSafeButton
                  key={index}
                  onClick={() =>
                    handleFileDownload(file.cdnPath, file.fileName)
                  }
                  className="w-full flex items-center gap-2 p-2 bg-gradient-to-r from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 rounded-lg transition-colors duration-200 text-left group border border-purple-100"
                  dir="auto"
                >
                  <span className="text-sm flex-shrink-0">
                    {getFileIcon(file.fileName)}
                  </span>
                  <p className="flex-1 text-sm font-medium text-gray-900 group-hover:text-purple-700 truncate text-start">
                    {file.displayName}
                  </p>
                  <HiOutlineDownload className="w-4 h-4 text-gray-400 group-hover:text-purple-500" />
                </PreviewSafeButton>
              ))}
            </div>
          )}
        </div>
      )}

      {(() => {
        const showSaveContact = businessCardData.allowVCardDownload === true;
        const showShare = !!processId;
        if (!showSaveContact && !showShare) return null;

        return (
          <div
            className={`grid gap-2 ${
              showSaveContact && showShare ? "grid-cols-2" : "grid-cols-1"
            }`}
          >
            {showSaveContact && (
              <VCardGenerator
                data={{
                  firstName: userData.first_name,
                  lastName: userData.last_name,
                  jobTitle: businessCardData.jobTitle,
                  email: userData.email,
                  phone: userData.phone,
                  description: businessCardData.description,
                  links: businessCardData.links,
                  avatar: userData.avatar,
                }}
                buttonColor="purple"
              />
            )}
            {showShare && (
              <BusinessCardShareButton
                processId={processId}
                size="md"
                variant="minimal"
                logoUrl={companyLogo}
              />
            )}
          </div>
        );
      })()}

      <BookingLinkButton
        booking={businessCardData.booking}
        entityIdentification={entityIdentification}
        memberId={userData.user_identification}
      />

      {enableUserRegistration && processId && entityIdentification && (
        <UserRegistrationSection
          processId={processId}
          entityData={{
            entity_identification: entityIdentification,
            entity_name: companyName,
            entity_logo: companyLogo,
          }}
          theme="classic"
          size="sm"
          onEmailLogin={handleFlipToEmail}
        />
      )}
    </div>
  );

  const frontContent = (
    <div className="w-full">
      <div
        className="
        w-full
        min-h-full
        relative
        rounded-xl overflow-hidden
      "
      >
        {headerContent && (
          <div className="row-start-1 row-end-2">{headerContent}</div>
        )}

        <div className="row-start-2 row-end-3">
          <div className="px-3 sm:px-4 py-3 sm:py-4">{mainContent}</div>
        </div>

        <div className="row-start-3 row-end-4">
          <BusinessCardFooter
            showFooter={businessCardData.showFooter as boolean}
            theme="creative"
          />
        </div>
      </div>
    </div>
  );

  return (
    <BusinessCardContainer
      isPreview={isPreview}
      className={`w-full h-auto min-h-0 ${className}`}
      frontContent={frontContent}
      isFlipping={isFlipping}
      showEmailLogin={showEmailLogin}
    />
  );
};

export default BusinessCardCreativeDisplay;
